/**
 * MgRunner — Wyścig Piesków (WP12c, PLAN §14).
 * Endless runner 60 s: tap=skok, zderzenie=otrzepanie, kości=gwiazdki.
 * Nagrody WYŁĄCZNIE przez core/RunClient; brak game-over.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import {
  buildRunStats,
  createRunnerState,
  GROUND_Y,
  isGoodRun,
  isShaking,
  PLAYER_H,
  PLAYER_W,
  PLAYER_X,
  remainingMs,
  returnSceneForEntry,
  RUNNER_DURATION_MS,
  RUNNER_GAME_ID,
  tick,
  tryJump,
  type RunnerEntity,
  type RunnerState,
} from './runnerLogic.js';

export type MgRunnerSceneData = {
  entryPoint?: 'phone' | 'console' | string;
  gameId?: string;
};

type EntityView = {
  root: Phaser.GameObjects.Container;
  kind: RunnerEntity['kind'];
};

export class MgRunnerScene extends BaseScene {
  static readonly KEY = 'MgRunner';

  private state!: RunnerState;
  private entryPoint: string | undefined;
  private runId: string | null = null;
  private finishing = false;
  private started = false;

  private player!: Phaser.GameObjects.Container;
  private dogBody!: Phaser.GameObjects.Arc;
  private starsText!: Phaser.GameObjects.Text;
  private timeText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private shakeText!: Phaser.GameObjects.Text;
  private entityViews = new Map<number, EntityView>();
  private groundScroll = 0;
  private groundTiles: Phaser.GameObjects.Rectangle[] = [];
  private spaceKey?: Phaser.Input.Keyboard.Key;
  private upKey?: Phaser.Input.Keyboard.Key;

  constructor() {
    super(MgRunnerScene.KEY);
  }

  init(data: MgRunnerSceneData = {}): void {
    this.entryPoint = data.entryPoint;
    this.runId = null;
    this.finishing = false;
    this.started = false;
    this.entityViews.clear();
    this.groundTiles = [];
  }

  create(data: MgRunnerSceneData = {}): void {
    this.entryPoint = data.entryPoint ?? this.entryPoint;
    this.state = createRunnerState({ worldW: this.gameW });
    this.cameras.main.setBackgroundColor(0xb8e6d0);

    this.drawSky();
    this.drawGround();
    this.spawnPlayer();
    this.buildHud();
    this.bindInput();

    this.events.once('shutdown', () => this.teardown());

    void this.beginRun();
  }

  update(_time: number, delta: number): void {
    if (!this.started || this.finishing || this.state.phase !== 'playing') return;

    if (this.spaceKey?.isDown || this.upKey?.isDown) {
      // edge: only jump on just-down via Phaser justDown
    }
    if (
      (this.spaceKey && Phaser.Input.Keyboard.JustDown(this.spaceKey)) ||
      (this.upKey && Phaser.Input.Keyboard.JustDown(this.upKey))
    ) {
      this.doJump();
    }

    const events = tick(this.state, delta);
    this.syncEntities();
    this.syncPlayerVisual();
    this.scrollGround(delta);
    this.refreshHud();

    for (const id of events.collectedBoneIds) {
      this.fxCollect(id);
    }
    for (const id of events.obstacleHits) {
      this.fxShake(id);
    }

    // tick mutuje phase → finished (TS nie widzi side-effect)
    if ((this.state.phase as RunnerState['phase']) === 'finished') {
      void this.completeRun('time');
    }
  }

  private async beginRun(): Promise<void> {
    try {
      const { runId } = await runClient.startRun(RUNNER_GAME_ID);
      this.runId = runId;
    } catch {
      // offline / stub — lokalny runId; finish i tak spróbuje
      this.runId = `local-runner-${Date.now()}`;
    }
    this.started = true;
  }

  private drawSky(): void {
    // pastele nieba + pagórki (placeholder, bez assetów)
    this.add.rectangle(this.gameW / 2, 160, this.gameW, 320, 0xd4f1f9);
    this.add.ellipse(200, 280, 280, 100, 0xa8d8b9, 0.7);
    this.add.ellipse(520, 300, 320, 110, 0x9fd4b0, 0.65);
    this.add.ellipse(900, 270, 360, 120, 0xa8d8b9, 0.7);
    this.add.ellipse(1100, 300, 240, 90, 0x9fd4b0, 0.6);
    this.add
      .text(this.gameW / 2, 36, 'Wyścig Piesków', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(20);
  }

  private drawGround(): void {
    const gy = GROUND_Y + 40;
    this.add.rectangle(this.gameW / 2, gy + 80, this.gameW, 200, 0x8fbc8f);
    // scrollable stripes
    const tileW = 80;
    for (let x = 0; x < this.gameW + tileW * 2; x += tileW) {
      const r = this.add
        .rectangle(x, GROUND_Y + 8, tileW - 4, 16, 0x7aaa7a)
        .setOrigin(0.5, 0)
        .setDepth(1);
      this.groundTiles.push(r);
    }
    // linia trawy
    this.add
      .rectangle(this.gameW / 2, GROUND_Y, this.gameW, 6, 0x6b9b6b)
      .setDepth(2);
  }

  private spawnPlayer(): void {
    const body = this.add.circle(0, 0, PLAYER_W / 2 - 4, 0xffe4c4).setStrokeStyle(3, 0xffffff);
    const earL = this.add.ellipse(-18, -28, 18, 28, 0xf5d0b0).setStrokeStyle(2, 0xffffff);
    const earR = this.add.ellipse(18, -28, 18, 28, 0xf5d0b0).setStrokeStyle(2, 0xffffff);
    const eyeL = this.add.circle(-12, -6, 5, 0x4a3f55);
    const eyeR = this.add.circle(12, -6, 5, 0x4a3f55);
    const nose = this.add.circle(0, 6, 6, 0x4a3f55);
    const label = this.add
      .text(0, 38, '🐕', { fontSize: '28px' })
      .setOrigin(0.5);
    this.dogBody = body;
    this.player = this.add.container(PLAYER_X, this.state.playerY, [
      earL,
      earR,
      body,
      eyeL,
      eyeR,
      nose,
      label,
    ]);
    this.player.setDepth(10);
    this.player.setSize(PLAYER_W, PLAYER_H);
  }

  private buildHud(): void {
    this.starsText = this.add
      .text(this.gameW - 40, 36, '⭐ 0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5)
      .setDepth(30);

    this.timeText = this.add
      .text(this.gameW / 2, 80, this.formatTime(RUNNER_DURATION_MS), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(30);

    this.hintText = this.add
      .text(this.gameW / 2, this.gameH - 36, 'Dotknij ekranu, by skoczyć! 🦴 = gwiazdki', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setDepth(30);

    this.shakeText = this.add
      .text(this.gameW / 2, 140, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#c45c84',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(30)
      .setAlpha(0);

    // wyjście w każdej chwili — duży cel
    UiKit.button(this, {
      x: 110,
      y: 48,
      label: 'Wyjdź',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xffd6a5,
      onClick: () => {
        void this.completeRun('exit');
      },
    }).setDepth(40);

    // duży niewidzialny obszar skoku (cała dolna/środkowa część)
    const jumpZone = this.add
      .rectangle(this.gameW / 2, this.gameH / 2 + 40, this.gameW, this.gameH - 80, 0x000000, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(5);
    jumpZone.on('pointerdown', () => this.doJump());
  }

  private bindInput(): void {
    if (this.input.keyboard) {
      this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.upKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    }
  }

  private doJump(): void {
    if (!this.started || this.finishing) return;
    if (tryJump(this.state)) {
      this.tweens.add({
        targets: this.player,
        scaleX: 1.05,
        scaleY: 0.92,
        duration: 80,
        yoyo: true,
      });
    }
  }

  private syncPlayerVisual(): void {
    this.player.setPosition(PLAYER_X, this.state.playerY);
    if (isShaking(this.state)) {
      const wobble = Math.sin(this.state.elapsedMs / 30) * 6;
      this.player.setX(PLAYER_X + wobble);
      this.dogBody.setFillStyle(0xffd0e0);
    } else {
      this.dogBody.setFillStyle(0xffe4c4);
    }
  }

  private scrollGround(delta: number): void {
    const speed = this.state.scrollSpeed * (delta / 1000);
    this.groundScroll = (this.groundScroll + speed) % 80;
    for (let i = 0; i < this.groundTiles.length; i++) {
      const tile = this.groundTiles[i]!;
      let x = i * 80 - this.groundScroll;
      if (x < -40) x += this.groundTiles.length * 80;
      tile.setX(x);
    }
  }

  private syncEntities(): void {
    const alive = new Set(this.state.entities.map((e) => e.id));
    for (const [id, view] of this.entityViews) {
      if (!alive.has(id)) {
        view.root.destroy(true);
        this.entityViews.delete(id);
      }
    }
    for (const e of this.state.entities) {
      let view = this.entityViews.get(e.id);
      if (!view) {
        view = this.spawnEntityView(e);
        this.entityViews.set(e.id, view);
      }
      view.root.setPosition(e.x, e.y);
    }
  }

  private spawnEntityView(e: RunnerEntity): EntityView {
    if (e.kind === 'bone') {
      const bg = this.add.circle(0, 0, 26, 0xfff3b0).setStrokeStyle(3, 0xffffff);
      const icon = this.add.text(0, 0, '🦴', { fontSize: '32px' }).setOrigin(0.5);
      const root = this.add.container(e.x, e.y, [bg, icon]).setDepth(8);
      return { root, kind: 'bone' };
    }
    const body = this.add
      .rectangle(0, 0, e.w, e.h, 0xc4a484)
      .setStrokeStyle(3, 0xffffff);
    const face = this.add.text(0, -4, '🪨', { fontSize: '28px' }).setOrigin(0.5);
    const root = this.add.container(e.x, e.y, [body, face]).setDepth(8);
    return { root, kind: 'obstacle' };
  }

  private fxCollect(id: number): void {
    const view = this.entityViews.get(id);
    if (view) {
      this.tweens.add({
        targets: view.root,
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        y: view.root.y - 40,
        duration: 220,
        onComplete: () => {
          view.root.destroy(true);
          this.entityViews.delete(id);
        },
      });
    }
    this.tweens.add({
      targets: this.starsText,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 100,
      yoyo: true,
    });
  }

  private fxShake(_id: number): void {
    this.cameras.main.shake(280, 0.008);
    this.shakeText.setText('Otrzepanie! ✨');
    this.shakeText.setAlpha(1);
    this.tweens.add({
      targets: this.shakeText,
      alpha: 0,
      duration: 700,
      delay: 200,
    });
  }

  private refreshHud(): void {
    this.starsText.setText(`⭐ ${this.state.stars}`);
    this.timeText.setText(this.formatTime(remainingMs(this.state)));
  }

  private formatTime(ms: number): string {
    const s = Math.ceil(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `⏱ ${m}:${r.toString().padStart(2, '0')}`;
  }

  private async completeRun(reason: 'time' | 'exit'): Promise<void> {
    if (this.finishing) return;
    this.finishing = true;

    if (this.state.phase === 'playing') {
      tick(this.state, 0, { forceFinish: true });
    }

    // krótki komunikat końca (nigdy „przegrana”)
    const good = isGoodRun(this.state.stars);
    this.hintText.setText(
      reason === 'exit'
        ? good
          ? 'Kończymy — super bieg! 🌟'
          : 'Kończymy — brawo za próbę! 💖'
        : good
          ? 'Meta! Świetny bieg! 🌟'
          : 'Meta! Brawo! 💖',
    );

    const stats = buildRunStats(this.state);
    let rewardZl = 0;
    const runId = this.runId ?? `local-runner-${Date.now()}`;

    try {
      const result = await runClient.finishRun(runId, stats);
      rewardZl = result.rewardZl;
    } catch {
      rewardZl = 0;
    }

    const returnTo = returnSceneForEntry(this.entryPoint);
    getSceneRouter().go('Reward', { rewardZl, returnTo, stars: stats.stars });
  }

  private teardown(): void {
    this.entityViews.forEach((v) => v.root.destroy(true));
    this.entityViews.clear();
    this.spaceKey = undefined;
    this.upKey = undefined;
  }
}

export default MgRunnerScene;
